import { useRef, type ReactNode } from "react";
import { Button } from "./Button";

export type FilePickerProps = {
  label: ReactNode;
  accept: string;
  onPick: (file: File) => void;
  /** Refused before the round trip, with the message the caller wants shown. */
  maxBytes?: number;
  onTooLarge?: (file: File) => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: ReactNode;
};

/** A button that opens the file picker. The input itself is hidden from both the
 * page and the accessibility tree, which is the standard idiom: browsers will not
 * style a file input, and a labelled button is what a person can actually see and
 * a screen reader can actually announce. */
export function FilePicker({ label, accept, onPick, maxBytes, onTooLarge, disabled, loading, icon }: FilePickerProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (!file) return;
          // Checked here so an oversized phone photo is refused instantly rather
          // than after a minute of uploading.
          if (maxBytes && file.size > maxBytes) { onTooLarge?.(file); return; }
          onPick(file);
        }}
      />
      <Button variant="secondary" icon={icon} disabled={disabled} loading={loading} onClick={() => input.current?.click()}>
        {label}
      </Button>
    </>
  );
}
